/**
 * @fileoverview The tray as a query, shared by the page that sorts it and the button that counts it, and the few
 * ways the page changes what it holds.
 *
 * The tray is the server's photos and the piles it makes of them (GET /tray; audit batch 6a): the app draws the
 * piles (drawPiles) and decides none of them. Its words (each pile's day, the "same as" offer's plant and day) are
 * in the app's language, which is part of the query's key.
 *
 * It is read only where it is drawn (W24 Job C): the tray page (/plants/photos) and "Check and save" while it is open
 * (CheckAndSave passes its `open`). A screen that only keeps the sheet mounted closed reads nothing; the invalidations
 * below read it again only where it is drawn, and the next open reads it.
 *
 * Where: PhotoTray, TrayPiles and CheckAndSave (useTray), TrayUploader and GooglePhotosResume (TRAY_QUERY_KEY,
 * trayPhotoLanded), PileCard (DrawnPile).
 * API: GET /tray (getTray).
 * Settings: TRAY_STALE_MS (config/tunables/garden).
 *
 * @module useTray
 */

import { useQuery, type QueryClient, type UseQueryResult } from '@tanstack/react-query';
import { getTray, type Tray, type TrayPhoto, type TrayPile } from '@/shared/api/tray';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { TRAY_STALE_MS } from '@/config/tunables/garden';

/** The tray's cache key, before the language: the uploader and the page invalidate by it, which reaches every language's copy. */
export const TRAY_QUERY_KEY = ['tray'] as const;

/** A pile as the page draws it: the server's pile with its photos and lead photo looked up. */
export interface DrawnPile extends Omit<TrayPile, 'lead'> {
  /** Its photos, oldest first, removed ones left out. */
  photos: TrayPhoto[];
  /** The server's lead photo, or the first one still in it when the lead was removed. */
  lead: TrayPhoto;
}

/**
 * The photos waiting on the tray and their piles, once someone is signed in and the tray is drawn.
 *
 * @param wanted - Whether the screen draws the tray now (CheckAndSave: while it is open); the tray page and its piles
 *   draw it whenever they are mounted, the default.
 * @returns The query (GET /tray).
 */
export function useTray(wanted = true): UseQueryResult<Tray, Error> {
  const { mysqlUser } = useAuth();
  const { i18n: { language } } = useTranslation();
  return useQuery({
    queryKey: [...TRAY_QUERY_KEY, language],
    queryFn: getTray,
    enabled: wanted && Boolean(mysqlUser),
    staleTime: TRAY_STALE_MS,
  });
}

/**
 * The server's piles with their photos, for drawing. A removed photo is not drawn (once removed, it is removed:
 * B45, owner 2026-10-21; the server keeps it for its own 24 hours, the app shows no trace). A pile whose photos
 * are not all in the answer (a photo filed a moment ago) keeps the ones that are; one with none left is not drawn.
 *
 * @param tray - The tray, or nothing before it arrives.
 * @returns The piles, in the server's order.
 */
export function drawPiles(tray: Tray | undefined): DrawnPile[] {
  if (!tray) return [];
  const byId = new Map(tray.photos.map((photo) => [photo.tray_id, photo]));
  return tray.piles.flatMap((pile) => {
    const photos = pile.photoIds.map((id) => byId.get(id)).filter((photo): photo is TrayPhoto => photo !== undefined && !photo.removed);
    if (photos.length === 0) return [];
    const lead = photos.find((photo) => photo.tray_id === pile.lead) ?? photos[0];
    return [{ ...pile, photos, lead }];
  });
}

/**
 * Puts photos the server sent back (a guess, a packet reading, an edit) into the tray as it stands; their piles
 * come with the tray when it is read again.
 *
 * @param queryClient - The app's query client.
 * @param updated - The photos as the server now has them.
 */
export function putTrayPhotos(queryClient: QueryClient, updated: TrayPhoto[]): void {
  const byId = new Map(updated.map((photo) => [photo.tray_id, photo]));
  queryClient.setQueriesData<Tray>({ queryKey: TRAY_QUERY_KEY }, (current) => current && {
    ...current,
    photos: current.photos.map((photo) => byId.get(photo.tray_id) ?? photo),
  });
}

/**
 * Puts the tray the server answered with (a move, a removal, Undo) in place. It was answered in the app's language,
 * which is the copy on screen.
 *
 * @param queryClient - The app's query client.
 * @param tray - The tray as the server now has it.
 */
export function putTray(queryClient: QueryClient, tray: Tray): void {
  // A move's or removal's answer may not carry the strip of photos Grow filed (B160); the strip on screen stays until the tray is read again.
  queryClient.setQueriesData<Tray>({ queryKey: TRAY_QUERY_KEY }, (current) => ({ ...tray, filed: tray.filed ?? current?.filed }));
}

/**
 * A photo just put on the tray: the tray is read again, so it is shown in the pile the server puts it in as it
 * lands, not when the batch ends.
 *
 * @param queryClient - The app's query client.
 * @param photo - The photo as the tray has it.
 */
export function trayPhotoLanded(queryClient: QueryClient, photo: TrayPhoto): void {
  queryClient.setQueriesData<Tray>({ queryKey: TRAY_QUERY_KEY }, (current) => current && {
    ...current,
    photos: current.photos.some((each) => each.tray_id === photo.tray_id) ? current.photos : [...current.photos, photo],
  });
  void queryClient.invalidateQueries({ queryKey: TRAY_QUERY_KEY });
}

/**
 * A photo filed off the tray (seed packets, several kinds): gone from what is drawn at once, the tray then read again.
 *
 * @param queryClient - The app's query client.
 * @param trayId - The photo.
 */
export function trayPhotoFiled(queryClient: QueryClient, trayId: number): void {
  queryClient.setQueriesData<Tray>({ queryKey: TRAY_QUERY_KEY }, (current) => current && {
    ...current,
    photos: current.photos.filter((photo) => photo.tray_id !== trayId),
    piles: current.piles.filter((pile) => !(pile.photoIds.length === 1 && pile.photoIds[0] === trayId)),
  });
  void queryClient.invalidateQueries({ queryKey: TRAY_QUERY_KEY });
}
